import type { ProjectNavigationId } from "@shuttlerock/navigation-components";
import type { AnalyticsContext, AnalyticsPage } from "@shuttlerock/observability";
import { Routes } from "@shuttlerock/router";
import { matchPath, type createBrowserRouter } from "react-router";

type RouterState = ReturnType<typeof createBrowserRouter>["state"];
type RouteMatch = RouterState["matches"][number];

/**
 * The product area of a page: the route's URL slug in snake_case. Pages nested under a
 * Project tab take the tab's value. A page outside this list sends no area.
 */
type PageArea =
  | "home"
  | "projects"
  | "projects_all"
  | "projects_drafts"
  | "projects_active"
  | "projects_complete"
  | "creative_library"
  | "client"
  | "account_insights"
  | "brief"
  | "delivery"
  | "review"
  | "production"
  | "upload"
  | "overview"
  | "editor"
  | "past_work"
  | "profile"
  | "brands"
  | "creator"
  | "invitations"
  | "management";

// The tracking plan's tab rule: a project page counts toward the tab its header highlights.
const PROJECT_TAB_AREAS: Record<ProjectNavigationId, PageArea> = {
  overview: "overview",
  brief: "brief",
  delivery: "delivery",
  revision: "review",
  editor: "editor",
  production: "production",
  upload: "upload",
};

const GLOBAL_AREAS: [path: string, area: PageArea][] = [
  [Routes.Home, "home"],
  [Routes.Projects.Projects, "projects"],
  [Routes.Projects.All, "projects_all"],
  [Routes.Projects.DraftProjects, "projects_drafts"],
  [Routes.Projects.ActiveProjects, "projects_active"],
  [Routes.Projects.CompletedProjects, "projects_complete"],
  [Routes.SalesTools.CreativeLibrary, "creative_library"],
  [`${Routes.Admin.Clients}/*`, "client"],
  [Routes.Admin.AccountInsights, "account_insights"],
  [Routes.PastWork, "past_work"],
  [Routes.Auth.Profile, "profile"],
  [Routes.Customer.BrandProfile, "brands"],
  [Routes.Creator.Profile, "creator"],
  [Routes.Creator.Brief, "creator"],
  [Routes.Auth.AcceptInvite, "invitations"],
  [`${Routes.ReactAdmin}/*`, "management"],
];

// Paths whose parameters are secrets: an invitation's claim code accepts the invitation.
const SECRET_PATHS: string[] = [Routes.Auth.AcceptInvite];

/** A secret path is sent as its route pattern, wherever a URL carries it. */
export const redactPath = (pathname: string) =>
  SECRET_PATHS.find((path) => matchPath(path, pathname)) ?? pathname;

const projectTabOf = ({ route }: RouteMatch): ProjectNavigationId | undefined => {
  const { handle } = route;
  return typeof handle === "object" && handle !== null && "projectNavigation" in handle
    ? (handle.projectNavigation as { id?: ProjectNavigationId } | undefined)?.id
    : undefined;
};

function pageArea({ location, matches }: RouterState): PageArea | undefined {
  // As in the project layout, the deepest route naming a tab wins.
  const tab = matches
    .map(projectTabOf)
    .filter((id) => id !== undefined)
    .at(-1);
  if (tab) return PROJECT_TAB_AREAS[tab];
  return GLOBAL_AREAS.find(([path]) => matchPath(path, location.pathname))?.[1];
}

export function analyticsRoute(state: RouterState): Pick<AnalyticsContext, "pathname" | "page"> {
  const params = state.matches.at(-1)?.params ?? {};
  const page: AnalyticsPage = {
    page_area: pageArea(state),
    project_key: params.projectKey,
    concept_key: params.conceptKey,
    // A review page's creative is the Deliverable its downloads name.
    deliverable_key: params.creativeKey,
  };
  // Query and hash changes (filters, selections, anchors) stay on the same page.
  return { pathname: state.location.pathname, page };
}
